import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Performer, performerDisplayTypes, performerStatus, briefStatus } from 'src/app/store/performers.era';
import { singleSessionPerformersReselector} from 'src/app/store/reselectors/performers.reselector';
import { extractPropValuesAsSet,  getXYFromEventAndWidth } from 'src/app/helpers/helpers';
import { setUnion, setDifference, makeMapFromListOfObjectsAndKeyToProperty, sortByProp, makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { SessionPerformerInfo, SessionInfo } from 'src/app/store/reselectors/performerSponsors.reselector';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../dialog.confirmDelete.component';
import { EntitySelectorData, EntitySelectorDialogComponent } from '../entities/entity-selector.dialog.component';
import { ManagePerformerData, ManagePerformerComponent } from 'src/app/modules/manage/manage-performer.component';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Wrapper } from 'src/app/components/wrapper.component';
import { JoiEvent, Ranking } from 'globals/store/events.data';
import { Dictionary } from '@ngrx/entity';
import { RankingDialogData, RankSelectDialogComponent } from '../../modules/sponsors/rank-select.dialog.component';
import { StatusWrapperComponent } from '../wrappers/status-wrapper.component';
import { AddManageWrapperComponent } from '../wrappers/add-manage-wrapper.component';
import { StaticPerformerCardsComponent } from './static-performer-cards.component';
import { MenuHoverButtonComponent } from '../buttons/menu-hover-button.component';
import { PerformerRoleEditorComponent } from './performer-role.component';
import { PersonCardComponent } from '../cards/person-card.component';
import { PerformerCardComponent } from '../cards/performer-card.component';
import { CommonModule } from '@angular/common';
import { SortByIdsPipe } from 'src/app/services/pipes/sortByIds.pipe';
import { eventsFeature } from 'src/app/store/events.era';

@Component({
    selector: 'performers',
    templateUrl: './performers.html',
    styleUrls: ['./performers.component.scss', '../../modules/manage/entity-drag.scss'],
    standalone: true,
    imports: [CdkDragHandle, CdkDrag, CdkDropList, SortByIdsPipe, CommonModule, StatusWrapperComponent, PerformerCardComponent, PerformerRoleEditorComponent, MenuHoverButtonComponent, AddManageWrapperComponent]
})
export class PerformersComponent extends Wrapper<PerformersComponent> implements OnDestroy, OnInit, OnChanges {

  @Input() displayType
  @Input() sessionId: string
  @Input() programId: string
  @Input() showBriefStatus = false
  @Input() emptyText = 'No performers'
  @Input() performerIds: string[]
  @Input() fromManageDialog = false
  @Input() readOnly = false // extra readOnly variable for some display types
  @Input() staticPerformerData: { performers, performerIdsBySessionIds, performerStatusBySessionIds } = null // for snapshot view only
  @Input() initialLimit = 0

  limited = false
  ids: string[]
  displayTypes = performerDisplayTypes
  performerIdsToAdd: Set<string> = new Set() // only used with delayedSaving
  performerIdsToDelete: Set<string> = new Set() // only used with delayedSaving
  modifiedStatus: { [performerId: string]: string } = {}
  modifiedBriefStatus: { [performerId: string]: string } = {}
  performerStatus = performerStatus
  briefStatus = briefStatus
  performerInfos: SessionPerformerInfo[] = []
  sessionInfo: SessionInfo = null
  performers: { [pId: string]: Performer } = {}
  threeDotOptions = [{ name: 'Remove performer from session', id: 'delete' }, { name: 'Edit performer', id: 'edit' }]
  noPerformers = false
  event: JoiEvent
  performerRoles: { [key: string]: Ranking } = {}

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super()
    this.subCallback(this.store.select(eventsFeature.selectEvent), event => {
      this.event = event
      this.performerRoles = makeMapFromListOfObjectsAndKey(event?.performerRoles || [], 'id')
    })
  }

  ngOnInit() {
    // console.log({ displayType: this.displayType })
    this.limited = !!this.initialLimit
    if(!this.displayType) this.displayType = performerDisplayTypes.editableCard;
    this.subCallback(this.store.select(singleSessionPerformersReselector, this.sessionId), sessionInfo => {
      if (!this.sessionId || !sessionInfo) return
      this.sessionInfo = sessionInfo
      this.performerInfos = sessionInfo.performerInfos
      this.checkForPerformers()
    })
    this.subCallback(this.store.select('performers', 'entities'), performers => {
      this.performers = performers
      this.checkForPerformers()
    })
  }

  async editPerformerRole(performerId: string, event: MouseEvent) {
    event.stopPropagation()
    const roleId = this.sessionInfo.fullSession.performerRoles[performerId] || null
    const { y: top, x: left } = getXYFromEventAndWidth(event, 300)
    const position = { top, left }
    const data: RankingDialogData = {
      position,
      ranks: this.event.performerRoles,
      selectedRankId: roleId,
      type: 'Performer role'
    }
  
    const oldRanksSerialized = JSON.stringify(data.ranks)
    const { ranks, selectedRankId } = await openDialogGetResult<{ ranks: Ranking[], selectedRankId: string }>(this.dialog, RankSelectDialogComponent, { width: '300px', disableClose: false, data, backdropClass: 'clear' }) // mutates data in place
    console.log({ ranks, selectedRankId, data })
    if (JSON.stringify(ranks) !== oldRanksSerialized) {
      this.eventService.updatePerformerRoles(ranks)
    }
    if (selectedRankId === roleId) return
    this.sessionInfo.fullSession.performerRoles[performerId] = selectedRankId
    this.saveSession()
  }

  saveSession() {
    this.programService.updateSession(this.sessionInfo.programId, this.sessionInfo.fullSession)
  }


  checkForPerformers() { // Remember, performer ids are not removed from sessions when performers are removed from the event;  so we need workarounds like this
    if (this.performers && this.performerInfos) {
      this.noPerformers = !this.performerInfos.filter(({ performerId: id }) => !!this.performers[id]).length
    }
  }

  dropPerformerIds(event: CdkDragDrop<SessionPerformerInfo[]>) {
    const newIds = [...this.ids]
    moveItemInArray(newIds, event.previousIndex, event.currentIndex)
    if (JSON.stringify(this.ids) === JSON.stringify(newIds)) return    
    this.ids = newIds
    const session = this.sessionInfo.fullSession
    session.performerIds = newIds
    this.programService.updateSession(this.programId, session)
    
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.performerIds) return
    const ids = changes.performerIds.currentValue ? [... changes.performerIds.currentValue] : [];
    
    if (JSON.stringify(this.ids) !== JSON.stringify(ids)) {
      // console.log({ ids: jsonCopyObject(ids) })
      this.ids = ids
    }
  }

  dotMenuSelection({ event, option }, performer: Performer) {
    switch (option) {
      case 'delete':
        this.deleteFromSessionWithConfirm(performer, event)
        break;
      case 'edit':
        this.editPerformer(performer)
        break;
      default:
        console.log(`Missing selection [${option}] from three dot menu`)
        break;
    }
  }

  trackByPInfo(_, pInfo: SessionPerformerInfo) {
    return pInfo.performerId
  }

  updateSessionInfo() {
    // since "session" / the sessionInfo can be updated at any time through the reselector, this needs to be called any time a temporary change is made (with delayed saving) or the reselector fires, also with delayed saving.  The reselector depends on persistent / saved data, so there needs to be some updating done here in the component itself for temp data.

    if (!this || !this.performers || !this.sessionInfo || !this.sessionId) return

    const union = setUnion<string>(extractPropValuesAsSet(this.sessionInfo.performerInfos, 'performerId'), this.performerIdsToAdd)
    const performerIds = Array.from(setDifference<string>(union, this.performerIdsToDelete)).filter(id => this.performers[id])
    this.performerInfos = performerIds.sort((a, b) => this.performers[a].name < this.performers[b].name ? -1 : 1).map(id => ({
      performerId: id,
      status: this.modifiedStatus[id] || this.sessionInfo.performerStatus[id],
      briefStatus: this.modifiedBriefStatus[id] || this.sessionInfo.performerBriefStatus[id],
    }))
    // console.log({ union, performerIds, status, performerIdsToAdd: this.performerIdsToAdd, performerIdsToDelete: this.performerIdsToDelete, performerInfos: this.sessionInfo.performerInfos })
  }

  getPerformerDataToManuallySave() { // sends data back
    this.updateSessionInfo() // to be safe
    // console.log({ sessionInfo: this.sessionInfo })
    // console.log({ performerInfos: this.performerInfos })
    return {
      performerIds: this.performerInfos.map(pInfo => pInfo.performerId),
      performerStatus: makeMapFromListOfObjectsAndKeyToProperty(this.performerInfos.filter(pInfo => pInfo.status), 'performerId', 'status'),
      performerBriefStatus: makeMapFromListOfObjectsAndKeyToProperty(this.performerInfos.filter(pInfo => pInfo.briefStatus), 'performerId', 'briefStatus')
    }
  }

  changeStatus(performerId: string, status: string) {
    this.performerService.changePerformerStatus(this.sessionInfo.id, this.sessionInfo.programId, status, performerId)
  }

  changeBriefStatus(performerId: string, status: string) {
    this.performerService.changePerformerBriefStatus(this.sessionInfo.id, this.sessionInfo.programId, status, performerId)
  }


  async editPerformer(performer: Performer) {

    const data: ManagePerformerData = { performerId: performer.id, readOnly: false }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManagePerformerComponent, { disableClose: true, autoFocus: false, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  async deleteFromSessionWithConfirm(performer: Performer, event) {
    const rect = event.target.getBoundingClientRect();
    const data: ConfirmDeleteData = {
      title: 'Remove ' + performer.name + ' from this session',
      deleteButtonText: 'REMOVE',
      rect,
      bottomOffset: -45,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.deleteFromSession(performer.id)

  }

  deleteFromSession(performerId) {
    // Deletion confirmation is nested in the status-wrapper component
    this.performerService.deletePerformerFromSession(this.sessionInfo, performerId)
    this.shareBriefsService.removePerformerFromSomeSessionShareables(performerId, this.sessionId)
  }

  addPerformersToASession(event) {
    const performers = this.sessionInfo.unusedPerformers.sort(sortByProp('name'))
    const data: EntitySelectorData = { entities: performers, entityName: 'Performer', searchPlaceholder: 'Search or add performer', rect: (<HTMLElement>event.target).getBoundingClientRect() }
    openDialogGetResult(this.dialog, EntitySelectorDialogComponent, { width: '300px', data, disableClose: true, backdropClass: 'clear' }).then((selectedPerformers: string[]) => {
      console.log({ selectedPerformers })
      if (!selectedPerformers) return
      this.performerService.addPerformersToProgramSession(this.sessionInfo, selectedPerformers)
    })
  }

}

